import { useEffect, useState, createContext, useContext } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Eye, EyeOff, Webhook, Link2, Unlink } from "lucide-react";

const FieldCtx = createContext(null);

const Badge = ({ on }) => (
  <span className={`text-[10px] uppercase tracking-[0.3em] ${on ? "text-[#86efac]" : "text-[#fcd34d]"}`}>
    {on ? "● Configured" : "○ Not configured"}
  </span>
);

const Field = ({ label, k, type = "text", placeholder, mask }) => {
  const { s, setS, show, setShow } = useContext(FieldCtx);
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">{label}</span>
      <div className="relative mt-2">
        <input
          type={mask && !show[k] ? "password" : type}
          value={s[k]}
          placeholder={placeholder || ""}
          onChange={(e) => setS({ ...s, [k]: e.target.value })}
          className="w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3 pr-12"
          data-testid={`settings-${k}`}
        />
        {mask && (
          <button
            type="button"
            onClick={() => setShow({ ...show, [k]: !show[k] })}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] hover:text-white"
          >
            {show[k] ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
          </button>
        )}
      </div>
    </label>
  );
};

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [s, setS] = useState({
    tmdb_api_key: "", radarr_url: "", radarr_api_key: "", radarr_quality_profile: "",
    sonarr_url: "", sonarr_api_key: "", sonarr_quality_profile: "",
    qbit_url: "", qbit_username: "", qbit_password: "",
    opensubs_api_key: "", trakt_client_id: "", trakt_client_secret: "",
    music_itunes_fallback: true, music_acoustid_enabled: false, acoustid_api_key: "",
    lan_base_url: "", webhook_secret: "",
  });
  const [info, setInfo] = useState({});
  const [saving, setSaving] = useState(false);
  const [traktStatus, setTraktStatus] = useState({ connected: false, username: "" });
  const [traktModal, setTraktModal] = useState(null);
  const [show, setShow] = useState({});
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwShow, setPwShow] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [enrich, setEnrich] = useState(null);
  const [enrichAuto, setEnrichAuto] = useState(null); // { enabled, last_run, next_run }
  const [identify, setIdentify] = useState(null);

  const load = async () => {
    if (!isAdmin) return;
    const [{ data }, { data: ts }] = await Promise.all([api.get("/settings"), api.get("/trakt/status").catch(() => ({ data: { connected: false } }))]);
    setS({
      tmdb_api_key: data.tmdb_api_key || "", radarr_url: data.radarr_url || "", radarr_api_key: data.radarr_api_key || "", radarr_quality_profile: data.radarr_quality_profile || "",
      sonarr_url: data.sonarr_url || "", sonarr_api_key: data.sonarr_api_key || "", sonarr_quality_profile: data.sonarr_quality_profile || "",
      qbit_url: data.qbit_url || "", qbit_username: data.qbit_username || "", qbit_password: data.qbit_password || "",
      opensubs_api_key: data.opensubs_api_key || "",
      trakt_client_id: data.trakt_client_id || "", trakt_client_secret: data.trakt_client_secret || "",
      music_itunes_fallback: data.music_itunes_fallback !== false,
      music_acoustid_enabled: !!data.music_acoustid_enabled,
      acoustid_api_key: data.acoustid_api_key || "",
      lan_base_url: data.lan_base_url || "",
      webhook_secret: data.webhook_secret || "",
    });
    setInfo(data); setTraktStatus(ts);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/settings", s); toast.success("Settings saved"); load(); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const testConn = async (kind) => {
    try { const { data } = await api.post(`/${kind}/test`); data.ok ? toast.success(`${kind} connected`) : toast.error(`${kind} unreachable`); }
    catch (err) { toast.error(err.response?.data?.detail || "Test failed"); }
  };

  const connectTrakt = async () => {
    try {
      const { data } = await api.post("/trakt/device-code");
      setTraktModal(data);
      // Poll
      const startedAt = Date.now();
      const interval = setInterval(async () => {
        if (Date.now() - startedAt > data.expires_in * 1000) { clearInterval(interval); setTraktModal(null); toast.error("Trakt code expired"); return; }
        try {
          const { data: r } = await api.post("/trakt/poll", { device_code: data.device_code });
          if (r.ok) { clearInterval(interval); setTraktModal(null); toast.success(`Trakt connected as ${r.username}`); load(); }
        } catch { clearInterval(interval); setTraktModal(null); toast.error("Trakt polling failed"); }
      }, (data.interval || 5) * 1000);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start Trakt flow"); }
  };

  const disconnectTrakt = async () => {
    await api.delete("/trakt/disconnect"); toast.success("Trakt disconnected"); load();
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change password"); }
    finally { setPwSaving(false); }
  };

  const toggleEnrichAuto = async () => {
    const enabled = !enrichAuto?.enabled;
    try {
      const { data } = await api.put("/tmdb/enrich-auto", { enabled });
      setEnrichAuto(data);
      toast.success(enabled ? "Auto-run on: enrichment will run every 24 hours" : "Auto-run off");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change auto-run"); }
  };

  const enrichAll = async () => {
    if (!window.confirm("Sweep all movies with missing metadata and fill from TMDB? This may take a while.")) return;
    try {
      await api.post("/tmdb/enrich-all");
      const { data } = await api.get("/tmdb/enrich-all/status");
      setEnrich(data);
      toast.success("Enrichment started");
    } catch (err) { toast.error(err.response?.data?.detail || "Enrichment failed"); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/tmdb/enrich-all/status").then(({ data }) => setEnrich(data)).catch(() => {});
    api.get("/tmdb/enrich-auto").then(({ data }) => setEnrichAuto(data)).catch(() => {});
    api.get("/music/identify/status").then(({ data }) => setIdentify(data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!enrich?.running) return;
    const t = setInterval(async () => {
      const { data } = await api.get("/tmdb/enrich-all/status");
      setEnrich(data);
      if (!data.running) {
        clearInterval(t);
        api.get("/tmdb/enrich-auto").then(({ data: a }) => setEnrichAuto(a)).catch(() => {});
        if (data.error) {
          toast.error(`Enrichment failed: ${data.error}`);
        } else {
          toast.success(`Enrichment done — ${data.enriched} enriched, ${data.skipped} already complete, ${data.failed} not found`);
        }
      }
    }, 2000);
    return () => clearInterval(t);
  }, [enrich?.running]);

  const identifyAll = async () => {
    if (!window.confirm("Fingerprint every track with an unknown artist or album and identify it via AcoustID? This may take a while.")) return;
    try {
      await api.post("/music/identify");
      const { data } = await api.get("/music/identify/status");
      setIdentify(data);
      toast.success("Identification started");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start"); }
  };

  useEffect(() => {
    if (!identify?.running) return;
    const t = setInterval(async () => {
      const { data } = await api.get("/music/identify/status");
      setIdentify(data);
      if (!data.running) {
        clearInterval(t);
        if (data.error) {
          toast.error(`Identification failed: ${data.error}`);
        } else {
          toast.success(`Identification done — ${data.identified} of ${data.total} tracks identified`);
        }
      }
    }, 2000);
    return () => clearInterval(t);
  }, [identify?.running]);

  return (
    <FieldCtx.Provider value={{ s, setS, show, setShow }}>
    <div className="min-h-screen bg-[#050505] pt-32 pb-24" data-testid="settings-page">
      <div className="px-6 md:px-12 max-w-3xl mx-auto">
        <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">{isAdmin ? "Admin" : "Account"}</span>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-white mt-3">Settings</h1>

        <form onSubmit={changePassword} className="mt-12 space-y-4 pb-10 border-b border-[#222]" data-testid="change-password-form">
          <h2 className="font-display text-2xl font-bold text-white">Password</h2>
          <p className="text-sm text-[#8A8A8A]">Requires your current password. Changing it locks out anyone who only had the old one.</p>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Current password</span>
            <div className="relative mt-2">
              <input
                type={pwShow ? "text" : "password"}
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                className="w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3 pr-12"
                data-testid="current-password"
              />
              <button type="button" onClick={() => setPwShow(!pwShow)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] hover:text-white">
                {pwShow ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">New password (min 8 characters)</span>
            <input
              type={pwShow ? "text" : "password"}
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3"
              data-testid="new-password"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Confirm new password</span>
            <input
              type={pwShow ? "text" : "password"}
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3"
              data-testid="confirm-password"
            />
          </label>
          <button type="submit" disabled={pwSaving} className="bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-60 text-white px-8 py-3 text-sm uppercase tracking-[0.2em]" data-testid="change-password-button">
            {pwSaving ? "Changing…" : "Change Password"}
          </button>
        </form>

        {isAdmin && (
        <form onSubmit={save} className="mt-12 space-y-10" data-testid="settings-form">
          {/* TMDB */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl font-bold text-white">TMDB</h2><Badge on={info.tmdb_configured} />
            </div>
            <p className="text-sm text-[#8A8A8A] mb-4">Free key at <a className="text-[#D9381E]" href="https://www.themoviedb.org/settings/api" target="_blank" rel="noreferrer">themoviedb.org</a></p>
            <Field label="API key (v3)" k="tmdb_api_key" mask />
            {info.tmdb_configured && (
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <button type="button" onClick={enrichAll} disabled={enrich?.running} className="text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-[#D9381E] hover:text-[#D9381E] text-[#8A8A8A] px-4 py-2 transition-colors disabled:opacity-50" data-testid="enrich-all-button">
                  {enrich?.running ? `Enriching… ${enrich.done}/${enrich.total}` : "Bulk enrich all movies →"}
                </button>
                <button type="button" role="switch" aria-checked={!!enrichAuto?.enabled} onClick={toggleEnrichAuto}
                  className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white" data-testid="enrich-auto-toggle"
                  title="When on, the TMDB enrichment runs by itself every 24 hours">
                  <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${enrichAuto?.enabled ? "bg-[#D9381E]" : "bg-[#222]"}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${enrichAuto?.enabled ? "translate-x-4" : ""}`} />
                  </span>
                  Auto-run every 24 h: {enrichAuto?.enabled ? "On" : "Off"}
                </button>
                {enrichAuto?.enabled && enrichAuto.next_run && (
                  <span className="text-xs text-[#8A8A8A]" data-testid="enrich-auto-next">
                    Next run: {new Date(enrichAuto.next_run) <= new Date() ? "shortly" : new Date(enrichAuto.next_run).toLocaleString()}
                  </span>
                )}
                {enrich && !enrich.running && enrich.error && (
                  <span className="text-xs text-[#fca5a5]" data-testid="enrich-all-error">
                    Last run failed: {enrich.error}
                  </span>
                )}
                {enrich && !enrich.running && !enrich.error && enrich.total > 0 && (
                  <span className="text-xs text-[#8A8A8A]" data-testid="enrich-all-summary">
                    Last run: {enrich.enriched} enriched · {enrich.skipped} already complete · {enrich.failed} not found on TMDB
                  </span>
                )}
              </div>
            )}
          </section>

          {/* Radarr */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl font-bold text-white">Radarr</h2><Badge on={info.radarr_configured} />
            </div>
            <Field label="Base URL" k="radarr_url" placeholder="http://192.168.1.10:7878" />
            <div className="mt-4"><Field label="API key" k="radarr_api_key" mask /></div>
            <div className="mt-4"><Field label="Quality profile for new requests" k="radarr_quality_profile" placeholder="HD-1080p No Remux" /></div>
            <button type="button" onClick={() => testConn("radarr")} className="mt-4 text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-4 py-2 transition-colors" data-testid="radarr-test">Test</button>
          </section>

          {/* Sonarr */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl font-bold text-white">Sonarr</h2><Badge on={info.sonarr_configured} />
            </div>
            <p className="text-sm text-[#8A8A8A] mb-4">Import your TV shows library.</p>
            <Field label="Base URL" k="sonarr_url" placeholder="http://192.168.1.10:8989" />
            <div className="mt-4"><Field label="API key" k="sonarr_api_key" mask /></div>
            <div className="mt-4"><Field label="Quality profile for new requests" k="sonarr_quality_profile" placeholder="HD-1080p No Remux" /></div>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => testConn("sonarr")} className="text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-4 py-2 transition-colors" data-testid="sonarr-test">Test</button>
              {info.sonarr_configured && <a href="/admin/sonarr" className="text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-[#D9381E] hover:text-[#D9381E] text-[#8A8A8A] px-4 py-2 transition-colors">Import shows →</a>}
            </div>
          </section>

          {/* qBittorrent */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl font-bold text-white">qBittorrent</h2><Badge on={info.qbit_configured} />
            </div>
            <p className="text-sm text-[#8A8A8A] mb-4">Powers the Downloads section on the Library page (pause all / resume all / live status).</p>
            <Field label="Base URL" k="qbit_url" placeholder="http://gluetun:8082" />
            <div className="mt-4"><Field label="Username" k="qbit_username" /></div>
            <div className="mt-4"><Field label="Password" k="qbit_password" mask /></div>
            <button type="button" onClick={() => testConn("qbit")} className="mt-4 text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-4 py-2 transition-colors" data-testid="qbit-test">Test</button>
          </section>

          {/* OpenSubtitles */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl font-bold text-white">OpenSubtitles</h2><Badge on={info.opensubs_configured} />
            </div>
            <p className="text-sm text-[#8A8A8A] mb-4">One-click subtitle search per movie/episode. Free key at <a className="text-[#D9381E]" href="https://www.opensubtitles.com/consumer/apps" target="_blank" rel="noreferrer">opensubtitles.com</a></p>
            <Field label="API key" k="opensubs_api_key" mask />
          </section>

          {/* Trakt */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl font-bold text-white">Trakt.tv</h2><Badge on={info.trakt_configured} />
            </div>
            <p className="text-sm text-[#8A8A8A] mb-4">Mirror watch history to Trakt. Register an app at <a className="text-[#D9381E]" href="https://trakt.tv/oauth/applications" target="_blank" rel="noreferrer">trakt.tv/oauth/applications</a> (use urn:ietf:wg:oauth:2.0:oob as redirect).</p>
            <Field label="Client ID" k="trakt_client_id" mask />
            <div className="mt-4"><Field label="Client secret" k="trakt_client_secret" mask /></div>
            {info.trakt_configured && (
              <div className="mt-4">
                {traktStatus.connected ? (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#86efac]">● Connected as {traktStatus.username}</span>
                    <button type="button" onClick={disconnectTrakt} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-[#fca5a5] hover:text-[#fca5a5] text-[#8A8A8A] px-4 py-2" data-testid="trakt-disconnect">
                      <Unlink size={12} /> Disconnect
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={connectTrakt} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] bg-[#D9381E] hover:bg-[#ED4B32] text-white px-4 py-2" data-testid="trakt-connect">
                    <Link2 size={12} /> Connect Trakt
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Auto-Import Webhooks */}
          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3 flex items-center gap-2">
              <Webhook size={20} strokeWidth={1.5} className="text-[#D9381E]" /> Auto-Import Webhooks
            </h2>
            <p className="text-sm text-[#8A8A8A] mb-4">
              New downloads land in the library automatically the moment Radarr/Sonarr finishes importing them —
              no manual step, no transcode queue. Add a Webhook connection in each app (Settings → Connect),
              triggered on <span className="text-white">On Import</span> and <span className="text-white">On Upgrade</span>,
              using the URL below for that app.
            </p>
            {s.webhook_secret ? (
              <div className="space-y-3 text-xs">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Radarr webhook URL</span>
                  <code className="block mt-1 bg-[#0F0F0F] border border-[#222] px-4 py-3 text-[#86efac] break-all" data-testid="radarr-webhook-url">
                    {window.location.origin}/api/webhooks/radarr?secret={s.webhook_secret}
                  </code>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Sonarr webhook URL</span>
                  <code className="block mt-1 bg-[#0F0F0F] border border-[#222] px-4 py-3 text-[#86efac] break-all" data-testid="sonarr-webhook-url">
                    {window.location.origin}/api/webhooks/sonarr?secret={s.webhook_secret}
                  </code>
                </div>
              </div>
            ) : (
              // GET /settings auto-generates the secret server-side, so this only shows
              // briefly while the initial load() request is in flight, not after a real save.
              <p className="text-xs text-[#666]">Loading webhook URLs…</p>
            )}
          </section>

          {/* Native Player (Kodi) */}
          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">Native Player (Kodi)</h2>
            <p className="text-sm text-[#8A8A8A] mb-4">
              "Play in Native Player" hands the raw file to Kodi via the StreamHoard Player helper. Kodi always runs on
              the home LAN — if left blank, the link uses whatever domain your browser session is on, which hairpins
              through the WAN (slow, can time out on large files) if that happens to be the public domain. Set this to
              StreamHoard's LAN address so Kodi always gets full LAN speed regardless of how you're browsing.
            </p>
            <Field label="LAN base URL (e.g. http://10.48.200.104:8080)" k="lan_base_url" />
          </section>

          {/* Music Metadata & Artwork */}
          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">Music Metadata &amp; Artwork</h2>
            <p className="text-sm text-[#8A8A8A] mb-4">
              Album art/tags come from MusicBrainz + Cover Art Archive first (free, no key). These add free fallbacks for what that misses.
            </p>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={s.music_itunes_fallback}
                onChange={(e) => setS({ ...s, music_itunes_fallback: e.target.checked })}
                className="w-4 h-4 accent-[#D9381E]"
                data-testid="settings-itunes-fallback"
              />
              <span className="text-sm text-white">Fall back to iTunes Search for artwork MusicBrainz doesn't have</span>
            </label>
            <p className="text-xs text-[#8A8A8A] mt-1 ml-7">No API key needed — always free, no signup.</p>

            <div className="mt-6 pt-6 border-t border-[#222]">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-white">AcoustID audio fingerprint identification</span>
                <Badge on={info.acoustid_configured} />
              </div>
              <p className="text-sm text-[#8A8A8A] mb-4">
                Identifies tracks with missing/unknown artist or album by fingerprinting the actual audio file — useful for badly-tagged files with nothing to text-search on. Free key at{" "}
                <a className="text-[#D9381E]" href="https://acoustid.org/api-key" target="_blank" rel="noreferrer">acoustid.org/api-key</a>.
              </p>
              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={s.music_acoustid_enabled}
                  onChange={(e) => setS({ ...s, music_acoustid_enabled: e.target.checked })}
                  className="w-4 h-4 accent-[#D9381E]"
                  data-testid="settings-acoustid-enabled"
                />
                <span className="text-sm text-white">Enable AcoustID identification</span>
              </label>
              <Field label="API key" k="acoustid_api_key" mask />
              {info.acoustid_configured && s.music_acoustid_enabled && (
                <div className="mt-4 flex items-center gap-4 flex-wrap">
                  <button type="button" onClick={identifyAll} disabled={identify?.running} className="text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-[#D9381E] hover:text-[#D9381E] text-[#8A8A8A] px-4 py-2 transition-colors disabled:opacity-50" data-testid="identify-all-button">
                    {identify?.running ? `Identifying… ${identify.done}/${identify.total}` : "Identify unknown tracks →"}
                  </button>
                  {identify && !identify.running && identify.error && (
                    <span className="text-xs text-[#fca5a5]" data-testid="identify-all-error">
                      Last run failed: {identify.error}
                    </span>
                  )}
                  {identify && !identify.running && !identify.error && identify.total > 0 && (
                    <span className="text-xs text-[#8A8A8A]" data-testid="identify-all-summary">
                      Last run: {identify.identified} of {identify.total} identified
                    </span>
                  )}
                </div>
              )}
            </div>
          </section>

          <button type="submit" disabled={saving} className="bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-60 text-white px-8 py-3 text-sm uppercase tracking-[0.2em]" data-testid="settings-save-button">
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </form>
        )}
      </div>

      {/* Trakt device-code modal */}
      {traktModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="trakt-modal">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={() => setTraktModal(null)} />
          <div className="relative bg-[#0F0F0F] border border-[#222] p-8 max-w-md w-full mx-4">
            <h2 className="font-display text-3xl font-bold text-white">Connect Trakt</h2>
            <p className="text-sm text-[#8A8A8A] mt-2">Visit the URL below, sign in to Trakt, and enter the code:</p>
            <a href={traktModal.verification_url} target="_blank" rel="noreferrer" className="block mt-6 text-[#D9381E] hover:text-[#ED4B32] break-all">{traktModal.verification_url}</a>
            <div className="mt-6 bg-black border border-[#D9381E] px-6 py-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Enter this code</div>
              <div className="font-display text-4xl font-black tracking-widest text-white mt-2" data-testid="trakt-user-code">{traktModal.user_code}</div>
            </div>
            <p className="text-xs text-[#8A8A8A] mt-4">Waiting for approval… this window will close automatically.</p>
          </div>
        </div>
      )}
    </div>
    </FieldCtx.Provider>
  );
}
